<template>
  <component
      :is="fieldComponent"
      ref="field"
      :label="label"
      :value="value"
      :readonly="proxiedDisabled"
      :disabled="proxiedDisabled"
      :error="error"
      v-bind="extProps"
      :fieldRule="fieldRule"
      :description="fieldRule && fieldRule.description"
      :uploaded.sync="uploaded"
      @blur="validateOnBlur"
      @input="input"
  />
</template>

<script>

import DpApplicationProfitCenterField from './DpApplicationProfitCenterField';
import DpApplicationTextField from './DpApplicationTextField';
import DpApplicationPersonalPhotoField from './DpApplicationPersonalPhotoField';
import DpApplicationConstructionTypeField from './DpApplicationConstructionTypeField';
import DpApplicationConstructionLocationField from './DpApplicationConstructionLocationField';
import DpApplicationConstructionTimeField from './DpApplicationConstructionTimeField';
import DpApplicationTpOvertimeApprovalPhotoField from './DpApplicationTpOvertimeApprovalPhotoField';
import DpApplicationPhoneNumberField from './DpApplicationPhoneNumberField.vue';
import DpApplicationPhotoField from './DpApplicationPhotoField';
import {mapState} from "pinia";
import useAuthnStore from "@/store/authn";
import useDpApplicationFormStore from "@/store/dp-application-form-store";

export default {
  name: "DpApplicationField",
  components: {
    DpApplicationPhotoField,
    DpApplicationTpOvertimeApprovalPhotoField,
    DpApplicationConstructionTimeField,
    DpApplicationConstructionLocationField,
    DpApplicationConstructionTypeField,
    DpApplicationPersonalPhotoField,
    DpApplicationTextField,
    DpApplicationProfitCenterField
  },
  props: {
    type: String,
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false
    },
    value: null,
    fieldRule: Object,
  },
  data() {
    return {
      error: false,
      latestValue: null,
      uploaded: true,
    }
  },
  computed: {
    ...mapState(useAuthnStore, ["isTenant"]),
    proxiedDisabled() {
      console.log('isTenant: ', this.isTenant)
      switch (this.type) {
        case 'TENANT_COMPANY':
        case 'PERSONAL_NAME':
        case 'PHONE_NUMBER':
          return this.isTenant ? true : this.disabled
        case 'BRAND':
          return true;
        default:
          return this.disabled
      }
    },
    fieldExtProps() {
      return {
        PROJECT_ID: {
          dpErrorMessage: `请选择${this.label}`
        },
        //施工類型
        CONSTRUCTION_TYPE: {
          dpErrorMessage: `请选择${this.label}`
        },
        //施工位置
        CONSTRUCTION_LOCATION: {
          dpErrorMessage: `请选择${this.label}`
        },
        //施工单位
        CONSTRUCTOR: {
          dpErrorMessage: `请输入${this.label}`,
          type: 'textarea',
          maxlength: '64',
        },
        //施工内容
        CONSTRUCTION_CONTENT: {
          dpErrorMessage: `请输入${this.label}`,
          type: 'textarea',
          maxlength: '64',
        },
        //施工开始时间
        CONSTRUCTION_START_TIME: {
          dpErrorMessage: `请选择${this.label}`,
        },
        //施工结束时间
        CONSTRUCTION_END_TIME: {
          dpErrorMessage: `请选择${this.label}`,
          end: true,
        },
        //人员姓名
        PERSONAL_NAME: {
          dpErrorMessage: `请输入${this.label}`,
          maxlength: '32',
        },
        //手机号码
        PHONE_NUMBER: {
          dpErrorMessage: `请输入${this.label}`,
          type: 'digit',
          maxlength: '11',
        },
        //身份证正面照
        ID_CARD_PHOTO_FRONT: {
          dpErrorMessage: `请上传${this.label}`,
        },
        //身份证背面照
        ID_CARD_PHOTO_BACK: {
          dpErrorMessage: `请上传${this.label}`,
        },
        //人员照片
        PERSONAL_PHOTO: {
          dpErrorMessage: `请上传${this.label}`,
        },
        //租户公司
        TENANT_COMPANY: {
          dpErrorMessage: `请输入${this.label}`,
          type: 'textarea',
          maxlength: '64'
        },
        //tp加班审批单
        TP_OVERTIME_APPROVAL_PHOTO: {
          dpErrorMessage: `请上传${this.label}`,
        }
      }
    },
    end() {
      return this.type === 'CONSTRUCTION_END_TIME'
    },
    extProps() {
      return this.fieldExtProps[this.type]
    },
    fieldComponent() {
      switch (this.type) {
        case 'PROJECT_ID':
          return DpApplicationProfitCenterField
        case 'CONSTRUCTION_TYPE':
          return DpApplicationConstructionTypeField
        case 'TP_OVERTIME_APPROVAL_PHOTO':
        case 'PHOTOS':
          return DpApplicationTpOvertimeApprovalPhotoField
        case 'PERSONAL_PHOTO':
          return DpApplicationPersonalPhotoField
        case 'ID_CARD_PHOTO_FRONT':
        case 'ID_CARD_PHOTO_BACK':
          return DpApplicationPhotoField
        case 'CONSTRUCTION_START_TIME':
          return DpApplicationConstructionTimeField
        case 'CONSTRUCTION_END_TIME':
          return DpApplicationConstructionTimeField
        case 'CONSTRUCTION_LOCATION':
          return DpApplicationConstructionLocationField
        case 'PHONE_NUMBER':
          return DpApplicationPhoneNumberField
        case 'CONSTRUCTOR':
        case 'CONSTRUCTION_CONTENT':
        case 'TENANT_COMPANY':
        case 'PERSONAL_NAME':
        default:
          return DpApplicationTextField
      }
    }
  },
  methods: {
    input(value) {
      // if (this.latestValue !== null && value !== null
      //     || this.latestValue !== value) {
      //   this.validate(value)
      //       .catch(() => {
      //       })
      //   this.latestValue = value
      // }
      this.$emit('input', value)
      useDpApplicationFormStore().cache()
    },
    validateOnBlur(value) {
      this.validate(value)
          .catch(() => {
          })
    },
    validate(value) {
      value = value ?? this.value

      // 如果后端传递了rule优先使用后端的rule，如果没有，默认必填
      if (this.fieldRule && this.fieldRule.required || !this.fieldRule) {
        if (!value || value?.length <= 0) {
          this.error = true
          return Promise.reject({
            ref: this.$refs.field,
            message: this.extProps.dpErrorMessage
          });
        }
      }

      if (!this.uploaded) {
        this.error = true
        return Promise.reject({
          ref: this.$refs.field,
          message: '请等待文件上传完毕，上传失败的图片请尝试重新上传'
        });
      }

      if (['PHONE_NUMBER'].includes(this.type)
          && !/^(1)[0-9]{10}$/.test(this.value)) {
        this.error = true
        return Promise.reject({
          ref: this.$refs.field,
          message: '手机号格式不正确，请重新输入'
        });
      }

      this.error = false
      return Promise.resolve()
    }
  }
}
</script>
